import { apiService } from '@/feature/api/axios';
import { handleApiError } from '@/shared/utils/errorUtils';
import { tokenStorage } from '@/shared/utils/tokenStorage';

import type {
  ILoginRequest,
  ILoginResponse,
  IApiLoginResponse,
  IUser,
  IGoogleOAuthInitResponse,
  IGoogleOAuthTokenLoginRequest,
  IGoogleOAuthResponse,
} from '../types/auth';

export const authService = {
  login: async (data: ILoginRequest): Promise<ILoginResponse> => {
    const api = apiService();
    // API returns: { message, token, user: { id, username, email, is_staff, is_superuser } }
    const apiResponse = await api.create<ILoginRequest, IApiLoginResponse>(
      '/api/ai-automation-admin/login/',
      data
    );

    // Map API response to frontend expected format
    // Use token as access token (if refresh token is not provided, use same token)
    const accessToken = apiResponse.token;
    const refreshToken = apiResponse.token; // Use same token as refresh if API doesn't provide separate one

    // Map API user to IUser structure
    // Extract additional fields if available
    const apiUser = apiResponse.user as Record<string, unknown>;
    const orgData =
      (apiUser.org as Record<string, unknown>) ||
      (apiUser.organization as Record<string, unknown>) ||
      {};

    let role: string = (apiUser.role as string) || 'user';
    if (apiResponse.user.is_superuser) {
      role = 'superuser';
    } else if (apiResponse.user.is_staff) {
      role = 'staff';
    }

    const mappedUser: IUser = {
      id: apiResponse.user.id,
      email: apiResponse.user.email || apiResponse.user.username,
      first_name: (apiUser.first_name as string) || (apiUser.firstName as string) || '',
      last_name: (apiUser.last_name as string) || (apiUser.lastName as string) || '',
      role,
      is_active: (apiUser.is_active as boolean) ?? (apiUser.isActive as boolean) ?? true,
      last_login:
        (apiUser.last_login as string) || (apiUser.lastLogin as string) || new Date().toISOString(),
      org: {
        id: (orgData.id as string) || '',
        name: (orgData.name as string) || '',
        org_logo: (orgData.org_logo as string) || '',
        is_active: (orgData.is_active as boolean) ?? true,
        subscription_plan: (orgData.subscription_plan as string) || '',
        show_selenium_script: (orgData.show_selenium_script as boolean) ?? false,
        subscription_expires_at: (orgData.subscription_expires_at as string) || '',
        integrations: {
          pms: {
            enabled: false,
            count: 0,
            integrations: [],
          },
          rms: {
            enabled: false,
            count: 0,
            integrations: [],
          },
        },
      },
    };

    // Store tokens securely
    tokenStorage.setAccessToken(accessToken);
    tokenStorage.setRefreshToken(refreshToken);

    // Return mapped response
    return {
      access: accessToken,
      refresh: refreshToken,
      user: mappedUser,
    };
  },

  logout: async (): Promise<void> => {
    try {
      const api = apiService();
      await api.create('/api/v1/logout/', {});
    } catch (error) {
      // Logout API error
      handleApiError(error, 'Logout failed. Please try again.');
    }

    tokenStorage.clearAllTokens();
  },

  getCurrentUser: async (): Promise<IUser | null> => {
    try {
      const api = apiService();
      const response = await api.get<IUser>('/api/v1/user/profile/');
      return response;
    } catch (error) {
      // Get current user API error
      handleApiError(error, 'Failed to get user profile. Please try again.');
      return null;
    }
  },

  // Google OAuth methods
  getGoogleOAuthUrl: async (): Promise<string> => {
    try {
      const api = apiService();
      const response = await api.get<IGoogleOAuthInitResponse>('api/v1/auth/google/init/');
      return response.authorization_url;
    } catch (error) {
      handleApiError(error, 'Failed to get Google OAuth URL. Please try again.');
      throw error;
    }
  },

  getMicrosoftOAuthUrl: async (): Promise<string> => {
    try {
      const api = apiService();
      const response = await api.get<IGoogleOAuthInitResponse>('api/v1/auth/microsoft/init/');
      return response.authorization_url;
    } catch (error) {
      handleApiError(error, 'Failed to get Microsoft OAuth URL. Please try again.');
      throw error;
    }
  },

  handleGoogleCallback: async (code: string): Promise<IGoogleOAuthResponse> => {
    try {
      const api = apiService();
      const response = await api.get<IGoogleOAuthResponse>(
        `/api/auth/google/callback/?code=${code}`
      );

      // Store access and refresh tokens securely
      tokenStorage.setAccessToken(response.access);
      tokenStorage.setRefreshToken(response.refresh);

      return response;
    } catch (error) {
      handleApiError(error, 'Google OAuth callback failed. Please try again.');
      throw error;
    }
  },

  loginWithGoogleIdToken: async (idToken: string): Promise<IGoogleOAuthResponse> => {
    try {
      const api = apiService();
      const data: IGoogleOAuthTokenLoginRequest = { id_token: idToken };
      const response = await api.create<IGoogleOAuthTokenLoginRequest, IGoogleOAuthResponse>(
        '/api/auth/google/token-login/',
        data
      );

      // Store access and refresh tokens securely
      tokenStorage.setAccessToken(response.access);
      tokenStorage.setRefreshToken(response.refresh);

      return response;
    } catch (error) {
      handleApiError(error, 'Google ID token login failed. Please try again.');
      throw error;
    }
  },
};
